<template>
  <div class="y-tabs">
    <div class="y-tabs-nav" ref="navRef">
      <div
        v-for="(tab, index) in registeredTabs"
        :key="tab.uid"
        class="y-tabs-item"
        :class="{ 'is-active': activeIndex === index }"
        @click="setActive(index)"
      >
        <span class="y-tabs-item-text">{{ tab.label }}</span>
      </div>
      <div class="y-tabs-indicator" :style="indicatorStyle"></div>
    </div>
    <div class="y-tabs-content">
      <slot></slot>
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  provide,
  onMounted,
  onUnmounted,
  watch,
  reactive,
  nextTick,
  onActivated
} from "vue";

const props = defineProps({
  modelValue: { type: [String, Number], default: 0 }
});
const emit = defineEmits(["update:modelValue"]);

const navRef = ref(null);
const activeIndex = ref(props.modelValue);
const registeredTabs = reactive([]);
const indicatorStyle = ref({});

let isFirstTimeVisible = false; // 标记是否已经首次可见
let visibilityObserver = null;

const setActive = index => {
  activeIndex.value = index;
  emit("update:modelValue", index);
};

const registerTab = tabInstance => {
  // 避免热更新时的重复注册
  const exists = registeredTabs.some(t => t.uid === tabInstance.uid);
  if (!exists) {
    registeredTabs.push(tabInstance);
  }
};

provide("registerTab", registerTab);
provide("activeIndex", activeIndex);
provide("registeredTabs", registeredTabs);

// 指示器更新函数
const updateIndicator = () => {
  // 只有在组件首次变得可见后才执行计算
  if (!isFirstTimeVisible) return;

  nextTick(() => {
    if (navRef.value && registeredTabs.length > 0 && activeIndex.value >= 0) {
      const activeTabEl = navRef.value.children[activeIndex.value];
      if (!activeTabEl) return;

      const textEl = activeTabEl.querySelector(".y-tabs-item-text");
      if (!textEl) return;

      const navRect = navRef.value.getBoundingClientRect();
      const textRect = textEl.getBoundingClientRect();

      // 如果 navRect.width 是 0，说明组件当前不可见，不进行计算
      if (navRect.width === 0) return;

      const textOffsetLeft = textRect.left - navRect.left;
      const textWidth = textRect.width;

      indicatorStyle.value = {
        width: `${textWidth}px`,
        transform: `translateX(${textOffsetLeft}px)`
      };
    }
  });
};

// 1. 监听 activeIndex 变化
watch(activeIndex, (newIndex, oldIndex) => {
  if (isFirstTimeVisible && newIndex !== oldIndex) {
    updateIndicator();
  }
});

// 2. 监听子组件注册完成
watch(
  registeredTabs,
  () => {
    if (isFirstTimeVisible) {
      updateIndicator();
    }
  },
  { flush: "post" }
); // flush: 'post' 确保在 DOM 更新后再执行 watch 回调

// 3. 使用 IntersectionObserver 处理“首次可见”
onMounted(() => {
  if (navRef.value) {
    visibilityObserver = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !isFirstTimeVisible) {
        isFirstTimeVisible = true;
        updateIndicator(); // 触发首次精确计算

        // 首次可见后即可停止观察，后续更新由 watch 负责
        if (visibilityObserver && navRef.value) {
          visibilityObserver.unobserve(navRef.value);
        }
      }
    });
    visibilityObserver.observe(navRef.value);
  }
});

onUnmounted(() => {
  if (visibilityObserver && navRef.value) {
    // 组件卸载时确保停止观察
    visibilityObserver.unobserve(navRef.value);
  }
});

// 4. 处理 KeepAlive 场景
onActivated(() => {
  isFirstTimeVisible = true;
  updateIndicator(); // 重新激活时强制更新
});
</script>

<style lang="scss" scoped>
.y-tabs-nav {
  position: relative;
  display: flex;
}

.y-tabs-item {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10px 16px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  &.is-active {
    .y-tabs-item-text {
      color: #000b1e;
      font-family: var(--y-medium-font);
    }
  }

  .y-tabs-item-text {
    transition: color 0.3s;
    color: #0b0b0b;
    font-family: var(--y-regular-font);
  }
}

.y-tabs-indicator {
  position: absolute;
  bottom: 0;
  left: 0;
  height: 4px;
  background-color: #0b0b0b;
  transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
}
</style>
